/**
 * Mega Menu Horizontal Scroll Controller
 * Handles horizontal scrolling with arrow buttons for mega menu items
 */

class MegaMenuScroll {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    /** @type {HTMLElement} */
    this.container = container;

    /** @type {HTMLElement | null} */
    this.scrollWrapper = container.querySelector('.mega-menu-scroll-wrapper');
    /** @type {HTMLElement | null} */
    this.scrollContent = container.querySelector('.mega-menu-scroll-content');
    /** @type {HTMLButtonElement | null} */
    this.prevButton = container.querySelector('[data-scroll-button="prev"]');
    /** @type {HTMLButtonElement | null} */
    this.nextButton = container.querySelector('[data-scroll-button="next"]');

    if (!this.scrollContent) return;

    this._updateQueued = false;
    this._resizeObserver = null;
    this._wrapperResizeObserver = null;
    this._visibilityObserver = null;
    this._mutationObserver = null;
    this._submenuObserver = null;
    this._toggleObserver = null;

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupObservers();

    // Primera actualización al inicializar (y una extra por si el layout termina de asentarse)
    this.scheduleUpdate();
    setTimeout(() => this.scheduleUpdate(), 150);
  }

  /**
   * Agrupa múltiples eventos (scroll/resize/mutations) en una sola actualización por frame.
   * Esto evita recomputar estados de botones demasiadas veces.
   */
  scheduleUpdate() {
    if (this._updateQueued) return;
    this._updateQueued = true;

    requestAnimationFrame(() => {
      this._updateQueued = false;
      this.updateButtonStates();
    });
  }

  setupEventListeners() {
    if (!this.scrollContent) return;
    const scrollContent = /** @type {HTMLElement} */ (this.scrollContent);

    // Button click handlers
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scroll('prev'));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scroll('next'));
    }

    // Scroll event to update button states
    scrollContent.addEventListener('scroll', () => {
      this.scheduleUpdate();
    });

    // Keyboard navigation
    /** @param {KeyboardEvent} e */
    scrollContent.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' && this.prevButton && !this.prevButton.disabled) {
        e.preventDefault();
        this.scroll('prev');
      } else if (e.key === 'ArrowRight' && this.nextButton && !this.nextButton.disabled) {
        e.preventDefault();
        this.scroll('next');
      }
    });
  }

  setupObservers() {
    if (!this.scrollContent) return;
    const scrollContent = /** @type {HTMLElement} */ (this.scrollContent);

    // 1) Resize -> recalcular
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
      this._resizeObserver.observe(scrollContent);

      if (this.scrollWrapper) {
        this._wrapperResizeObserver = new ResizeObserver(() => this.scheduleUpdate());
        this._wrapperResizeObserver.observe(this.scrollWrapper);
      }
    }

    // 2) Cuando el contenedor se vuelve visible (p.ej. al abrir el mega menú)
    if (typeof IntersectionObserver !== 'undefined') {
      this._visibilityObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.scheduleUpdate();
          // Extra update por si el layout termina de asentarse justo al mostrarse
          setTimeout(() => this.scheduleUpdate(), 100);
        }
      }, { threshold: 0.01 });

      this._visibilityObserver.observe(this.container);
    }

    // 3) Cambios de atributos/clases al abrir/cerrar submenu
    /** @type {HTMLElement | null} */
    const parentSubmenu = this.container.closest('.menu-list__submenu');
    if (typeof MutationObserver !== 'undefined') {
      // Detectar cambios del propio contenedor (clases/estilos) que afecten layout
      this._mutationObserver = new MutationObserver(() => this.scheduleUpdate());
      this._mutationObserver.observe(this.container, {
        attributes: true,
        attributeFilter: ['style', 'class', 'aria-hidden']
      });

      if (parentSubmenu) {
        this._submenuObserver = new MutationObserver(() => {
          const isVisible =
            parentSubmenu.offsetParent !== null &&
            window.getComputedStyle(parentSubmenu).visibility !== 'hidden';

          if (isVisible) {
            this.scheduleUpdate();
            setTimeout(() => this.scheduleUpdate(), 100);
          }
        });

        this._submenuObserver.observe(parentSubmenu, {
          attributes: true,
          attributeFilter: ['style', 'class', 'aria-hidden', 'hidden']
        });
      }
    }

    // 4) Señales directas de “se abrió el submenu”: aria-expanded + interacción
    if (parentSubmenu) {
      const toggle = this.findSubmenuToggle(parentSubmenu);
      if (toggle && typeof MutationObserver !== 'undefined') {
        this._toggleObserver = new MutationObserver(() => {
          if (toggle.getAttribute('aria-expanded') === 'true') {
            this.scheduleUpdate();
            setTimeout(() => this.scheduleUpdate(), 100);
          }
        });

        this._toggleObserver.observe(toggle, {
          attributes: true,
          attributeFilter: ['aria-expanded']
        });
      }

      const handleLikelyOpen = () => {
        this.scheduleUpdate();
        setTimeout(() => this.scheduleUpdate(), 100);
      };

      parentSubmenu.addEventListener('mouseenter', handleLikelyOpen);
      parentSubmenu.addEventListener('focusin', handleLikelyOpen);
    }
  }

  /**
   * @param {HTMLElement} parentSubmenu
   * @returns {HTMLElement | null}
   */
  findSubmenuToggle(parentSubmenu) {
    // Buscar un toggle con aria-controls que apunte a este submenu
    const id = parentSubmenu.getAttribute('id');
    if (!id) return null;

    // Suele estar en el mismo item del menú (link/button con aria-controls)
    const li = parentSubmenu.closest('li');
    if (!li) return null;

    // CSS.escape no existe en todos los navegadores antiguos; fallback a ID sin escape.
    const safeId = (window.CSS && typeof window.CSS.escape === 'function') ? window.CSS.escape(id) : id;
    /** @type {HTMLElement | null} */
    const toggle = li.querySelector(`[aria-controls="${safeId}"]`);
    return toggle;
  }

  /**
   * @param {'prev' | 'next'} direction
   */
  scroll(direction) {
    if (!this.scrollContent) return;
    const scrollContent = /** @type {HTMLElement} */ (this.scrollContent);

    const scrollAmount = scrollContent.clientWidth * 0.8;
    const currentScroll = scrollContent.scrollLeft;
    const maxScroll = scrollContent.scrollWidth - scrollContent.clientWidth;

    let targetScroll;

    if (direction === 'prev') {
      targetScroll = Math.max(0, currentScroll - scrollAmount);
    } else {
      targetScroll = Math.min(maxScroll, currentScroll + scrollAmount);
    }

    scrollContent.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });

    // Durante el scroll suave, el evento `scroll` irá actualizando estados.
    // Programamos una actualización final por si el navegador no emite scroll al final.
    this.scheduleUpdate();
    setTimeout(() => this.scheduleUpdate(), 350);
  }

  updateButtonStates() {
    if (!this.scrollContent) return;

    const scrollContent = /** @type {HTMLElement} */ (this.scrollContent);
    const { scrollLeft, scrollWidth, clientWidth } = scrollContent;
    const isCoarsePointer =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(pointer: coarse)').matches;

    const hasScroll = scrollWidth > clientWidth;
    const isAtStart = scrollLeft <= 1; // Tolerancia de 1px para errores de redondeo
    const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1; // Tolerancia de 1px

    // Si no hay scroll disponible, ocultar ambos botones
    if (!hasScroll) {
      if (this.prevButton) {
        this.prevButton.style.display = 'none';
        this.prevButton.setAttribute('aria-hidden', 'true');
        this.prevButton.disabled = true;
      }
      if (this.nextButton) {
        this.nextButton.style.display = 'none';
        this.nextButton.setAttribute('aria-hidden', 'true');
        this.nextButton.disabled = true;
      }
      return;
    }

    // Botón previo (izquierda)
    if (this.prevButton) {
      // En pantallas táctiles (pointer: coarse) mantenemos el botón visible siempre que haya scroll
      // y solo cambiamos su estado habilitado/deshabilitado. En escritorio seguimos ocultando
      // cuando no se puede desplazar en esa dirección.
      if (isCoarsePointer) {
        this.prevButton.style.display = 'flex';
        this.prevButton.setAttribute('aria-hidden', String(!hasScroll));
        this.prevButton.disabled = isAtStart || !hasScroll;
      } else {
        if (isAtStart) {
          this.prevButton.style.display = 'none';
          this.prevButton.setAttribute('aria-hidden', 'true');
          this.prevButton.disabled = true;
        } else {
          this.prevButton.style.display = 'flex';
          this.prevButton.setAttribute('aria-hidden', 'false');
          this.prevButton.disabled = false;
        }
      }
      this.prevButton.setAttribute('aria-disabled', String(isAtStart));
    }

    // Botón siguiente (derecha)
    if (this.nextButton) {
      if (isCoarsePointer) {
        this.nextButton.style.display = 'flex';
        this.nextButton.setAttribute('aria-hidden', String(!hasScroll));
        this.nextButton.disabled = isAtEnd || !hasScroll;
      } else {
        if (isAtEnd) {
          this.nextButton.style.display = 'none';
          this.nextButton.setAttribute('aria-hidden', 'true');
          this.nextButton.disabled = true;
        } else {
          this.nextButton.style.display = 'flex';
          this.nextButton.setAttribute('aria-hidden', 'false');
          this.nextButton.disabled = false;
        }
      }
      this.nextButton.setAttribute('aria-disabled', String(isAtEnd));
    }
  }
}

// Initialize all mega menu scroll containers
function initializeMegaMenuScroll() {
  /** @type {NodeListOf<HTMLElement>} */
  const containers = document.querySelectorAll('[data-mega-menu-scroll]');
  containers.forEach((container) => {
    if (!container.dataset.initialized) {
      new MegaMenuScroll(container);
      container.dataset.initialized = 'true';
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initializeMegaMenuScroll();
    // Reintento corto por si el HTML se inyecta/cambia justo después del DOMContentLoaded
    setTimeout(initializeMegaMenuScroll, 200);
  });
} else {
  // DOM already loaded
  initializeMegaMenuScroll();
  setTimeout(initializeMegaMenuScroll, 200);
}

// También inicializar cuando las imágenes/recursos se carguen (cambios de layout)
window.addEventListener('load', () => {
  initializeMegaMenuScroll();
});

// Also initialize when new content is loaded dynamically
if (typeof CustomEvent !== 'undefined') {
  document.addEventListener('shopify:section:load', () => {
    initializeMegaMenuScroll();
  });
}



